import { SWIPE_BACK_OPTIONS } from "@/utils/enum";
import { BLOCKED_PAGES } from "next/dist/shared/lib/constants";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import { useBack } from "./useBack";

interface SwipeState {
  startX: number;
  startY: number;
  currentX: number;
  isSwipingBack: boolean;
  startTime: number;
}

const {
  threshold,
  maxVerticalMovement,
  edgeThreshold,
  velocityThreshold,
  disabled
} = SWIPE_BACK_OPTIONS;

export const useSwipeToBack = () => {
  const { goBack } = useBack();
  const router = useRouter();

  const swipeStateRef = useRef<SwipeState>({
    startX: 0,
    startY: 0,
    currentX: 0,
    isSwipingBack: false,
    startTime: 0
  });

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (disabled) return;

      const touch = e.touches[0];
      const startX = touch.clientX;
      const startY = touch.clientY;

      if (startX <= edgeThreshold) {
        swipeStateRef.current = {
          startX,
          startY,
          currentX: startX,
          isSwipingBack: true,
          startTime: Date.now()
        };
      }
    },
    [disabled, edgeThreshold]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (disabled || !swipeStateRef.current.isSwipingBack) return;

      const touch = e.touches[0];
      const currentX = touch.clientX;
      const currentY = touch.clientY;

      const deltaX = currentX - swipeStateRef.current.startX;
      const deltaY = Math.abs(currentY - swipeStateRef.current.startY);

      if (deltaY > maxVerticalMovement) {
        swipeStateRef.current.isSwipingBack = false;
        return;
      }

      if (deltaX > 0) {
        swipeStateRef.current.currentX = currentX;
      }
    },
    [disabled, maxVerticalMovement]
  );

  const handleTouchEnd = useCallback(() => {
    if (disabled || !swipeStateRef.current.isSwipingBack) return;

    const { startX, currentX, startTime } = swipeStateRef.current;
    const deltaX = currentX - startX;
    const deltaTime = Date.now() - startTime;
    const velocity = deltaX / deltaTime;

    if (deltaX > threshold || velocity > velocityThreshold) {
      goBack();
    }

    // Reset state
    swipeStateRef.current = {
      startX: 0,
      startY: 0,
      currentX: 0,
      isSwipingBack: false,
      startTime: 0
    };
  }, [disabled, threshold, velocityThreshold, goBack]);

  useEffect(() => {
    if (disabled || typeof window === "undefined") return;

    if (!("ontouchstart" in window)) return;

    if (BLOCKED_PAGES.includes(router.pathname)) {
      return;
    }

    document.addEventListener("touchstart", handleTouchStart, {
      passive: true
    });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [
    disabled,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    router.pathname
  ]);
};
